import { useEffect, useState } from "react";
import api from "@/lib/api";
import { RecruitingGate, isRecruitingRequired } from "@/components/Pricing";
import Header from "@/components/Header";
import { BarChart, Bar, XAxis, YAxis, ResponsiveContainer, Tooltip, CartesianGrid } from "recharts";

export default function MarketIntel() {
  const [intel, setIntel] = useState(null);
  const [gated, setGated] = useState(false);
  useEffect(() => { api.get("/market/intel").then(({ data }) => setIntel(data)).catch((e) => { if (isRecruitingRequired(e)) setGated(true); }); }, []);

  if (gated) return <Shell><div className="px-4 md:px-8 lg:px-12 py-8"><RecruitingGate feature="Market intelligence" /></div></Shell>;
  if (!intel) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="market-intel-page">
        <div className="text-xs tracking-widest text-volt">ANONYMIZED · AGGREGATED</div>
        <h1 className="heading text-4xl md:text-5xl mt-2">MARKET INTELLIGENCE</h1>
        <p className="text-neutral-400 mt-2">Live signals from the CoachConnect AI Temporal Knowledge Graph.</p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-neutral-800">
          <Stat label="Coaches in TKG" value={intel.total_coaches} />
          <Stat label="Live Openings" value={intel.total_openings} />
          <Stat label="Generated" value={new Date(intel.generated_at).toLocaleString()} small />
        </div>

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Panel title="DEMAND vs SUPPLY (BY SPORT × GENDER)" testid="demand-supply-chart">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={intel.demand_supply}>
                <CartesianGrid stroke="#27272A" strokeDasharray="2 4" />
                <XAxis dataKey="label" stroke="#71717A" style={{ fontSize: 10 }} />
                <YAxis stroke="#71717A" style={{ fontSize: 10 }} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", color: "white" }} />
                <Bar dataKey="coaches" fill="#007AFF" />
                <Bar dataKey="openings" fill="#FF3B30" />
              </BarChart>
            </ResponsiveContainer>
            <Legend items={[{ c: "#007AFF", l: "Coaches" }, { c: "#FF3B30", l: "Openings" }]} />
          </Panel>

          <Panel title="REGIONAL OPENING DEMAND" testid="region-chart">
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={intel.regions} layout="vertical">
                <CartesianGrid stroke="#27272A" strokeDasharray="2 4" />
                <XAxis type="number" stroke="#71717A" style={{ fontSize: 10 }} />
                <YAxis dataKey="region" type="category" stroke="#71717A" style={{ fontSize: 10 }} width={110} />
                <Tooltip contentStyle={{ background: "#121212", border: "1px solid #27272A", color: "white" }} />
                <Bar dataKey="openings" fill="#FF3B30" />
              </BarChart>
            </ResponsiveContainer>
          </Panel>
        </div>

        <div className="mt-6 border border-neutral-800 bg-appcard">
          <div className="px-6 py-4 border-b border-neutral-800 text-xs tracking-widest text-volt">SALARY BENCHMARKS BY DIVISION</div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-mono">
              <thead className="text-neutral-500 uppercase text-xs">
                <tr><th className="text-left p-4">Division</th><th className="text-left p-4">Salary Band</th><th className="text-right p-4">Openings</th></tr>
              </thead>
              <tbody>
                {intel.salary_benchmarks.length === 0 ? (
                  <tr><td colSpan={3} className="p-6 text-neutral-500 text-center">Post jobs to populate this table.</td></tr>
                ) : intel.salary_benchmarks.map((b, i) => (
                  <tr key={i} className="border-t border-neutral-800">
                    <td className="p-4">{b.division}</td>
                    <td className="p-4 text-neutral-300">{b.salary_band}</td>
                    <td className="p-4 text-right text-blaze">{b.openings}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </Shell>
  );
}
function Stat({ label, value, small }) {
  return (
    <div className="bg-appbg p-6">
      <div className="text-[10px] tracking-widest text-neutral-500">{label.toUpperCase()}</div>
      <div className={`heading mt-2 text-mono ${small ? "text-base" : "text-5xl"}`}>{value}</div>
    </div>
  );
}
function Panel({ title, children, testid }) {
  return (
    <div className="border border-neutral-800 bg-appcard p-4" data-testid={testid}>
      <div className="text-xs tracking-widest text-volt mb-3">{title}</div>
      {children}
    </div>
  );
}
function Legend({ items }) {
  return (
    <div className="flex gap-4 mt-2 text-xs">
      {items.map((i) => (
        <div key={i.l} className="flex items-center gap-1.5"><span className="w-3 h-3" style={{ background: i.c }} /> {i.l}</div>
      ))}
    </div>
  );
}
function Shell({ children }) { return <div className="min-h-screen bg-appbg text-white"><Header />{children}</div>; }
